<script setup>
import { reactive, onMounted, ref, computed, watch, nextTick } from 'vue'
import { useFormulario } from '@/composables/useFormulario'
import { useAuthStore } from '@/stores/auth'
import { useMetadataListasStore } from '@/stores/metadataListas'
import api from '@/services/api'
import ComexPipelineStatus from '@/components/base/fluxo/ComexPipelineStatus.vue'
import FormularioCadastro from '@/components/base/formulario/FormularioCadastro.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import CampoSelect from '@/components/base/campos/CampoSelect.vue'
import CampoBusca from '@/components/base/campos/CampoBusca.vue'
import CampoBuscaLista from '@/components/base/campos/CampoBuscaLista.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import ModalDeBusca from '@/components/base/modal/ModalDeBusca.vue'
import ModalFormulario from '@/components/base/modal/ModalFormulario.vue'
import ConfirmationModal from '@/components/base/modal/ConfirmationModal.vue'
import SemAcessoModulo from '@/components/base/util/SemAcessoModulo.vue'
import { filialSearch } from '@/search-definitions/filialSearch'
import { criarConfigBuscaCliente } from '@/search-definitions/clienteSearch'
import { templatePrecificacaoSearch } from '@/search-definitions/templatePrecificacaoSearch'
import { criarConfigBuscaProduto } from '@/search-definitions/produtoSearch'
import { criarConfigBuscaUnidadeMedidaProduto } from '@/search-definitions/produtoUnidadeMedidaSearch'
import { ST } from '@/constants/ST'
import { VENDA_ST_CONTRATO } from '@/constants/VENDA_ST_CONTRATO'
import { formatarDataBR } from '@/utils/useDataBR'

const props = defineProps({
  itemParaEditar: {
    type: Object,
    default: null
  },
  modeloVazio: {
    type: Object,
    default: () => ({
      idContratoVenda: 0,
      idOrganizacao: null,
      idFilial: null,
      idParceiroNegocioCliente: null,
      numeroContratoCliente: '',
      codigoContratoVenda: '',
      dataContrato: new Date().toISOString().split('T')[0],
      lstMoeda: '',
      lstStatusContratoVenda: VENDA_ST_CONTRATO.EmDigitacao,
      valorTotalContrato: null,
      percentualTolerancia: null,
      atributosPrecificacaoJson: null
    })
  },
  drilldownMode: {
    type: Boolean,
    default: false
  },
  pipelineProps: {
    type: Object,
    default: () => ({})
  },
  contextoPai: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits([
  'itemAtualizado',
  'cancelar',
  'show-notification',
  'drilldown-push',
  'drilldown-pop',
  'pipeline-navegar'
])

const authStore = useAuthStore()
const metadataStore = useMetadataListasStore()
const mostrarAuditoria = ref(false)
const btnTemplate = ref(null)

const listas = reactive({})

const accordionState = reactive({
  dadosBasicos: true,
  pricing: !props.itemParaEditar || !props.itemParaEditar.idContratoVenda,
  produtos: false,
  condicoesPagamento: false,
  pedidosVenda: true
})

const produtosContrato = ref([])
const isLoadingProdutos = ref(false)
const produtoEmEdicao = ref(null)

const condicoesPagamentoContrato = ref([])
const isLoadingCondicoes = ref(false)
const condicaoEmEdicao = ref(null)

const pedidosVendaContrato = ref([])
const isLoadingPedidosVenda = ref(false)

const pedidosVendaContratoOrdenados = computed(() => {
  return [...pedidosVendaContrato.value].sort((a, b) => {
    if (!a.dataEmissao) return 1
    if (!b.dataEmissao) return -1
    return new Date(a.dataEmissao) - new Date(b.dataEmissao)
  })
})

const showModalProduto = computed({
  get: () => !!produtoEmEdicao.value,
  set: (val) => { if (!val) produtoEmEdicao.value = null }
})

const showModalCondicao = computed({
  get: () => !!condicaoEmEdicao.value,
  set: (val) => { if (!val) condicaoEmEdicao.value = null }
})

const modalExclusaoProduto = ref({ id: null })
const modalExclusaoProdutoRef = ref(null)

const modalExclusaoCondicao = ref({ id: null })
const modalExclusaoCondicaoRef = ref(null)

const modalConfirmacaoTrocaRef = ref(null)

const {
  item,
  isLoading,
  isEditing,
  errors,
  focarPrimeiroErro,
  submitForm,
  initializeForm,
  handleCancel
} = useFormulario(props, emit)

// DEBUG: Rastrear evento cancelar
const handleCancelDebug = () => {
  console.log('[DEBUG ContratoVendaForm] handleCancel chamado. Propagando cancelar...')
  handleCancel()
}

const entityInfoContrato = computed(() => ({
  id: item.value.idContratoVenda,
  name: item.value.numeroContratoCliente,
  nameLabel: 'Nº Contrato'
}))

const podeEditar = computed(() => {
  if (authStore.isAdminInterno) return true
  if (authStore.permissoes && authStore.permissoes.includes('MODULO_COMPLETO')) return true
  return authStore.getPermsDoModulo('VENDAS').some(p => p === 'COMPLETO' || (p.includes('CONTRATOS_VENDA') && p.includes('COMPLETO')))
})

const temModuloVendas = computed(() => {
  return authStore.temModulo('VENDAS')
})

const temContratosVendaCompleto = computed(() => {
  if (authStore.isAdminInterno) return true
  if (authStore.pode('CONTRATOS_VENDA_COMPLETO')) return true
  return authStore.getPermsDoModulo('VENDAS').some(p =>
    p === 'COMPLETO' || (p.includes('CONTRATOS_VENDA') && p.includes('COMPLETO'))
  )
})

const temVisualizacao = computed(() => {
  if (authStore.isAdminInterno) return true
  if (authStore.pode('CONTRATOS_VENDA_VISUALIZACAO')) return true
  return authStore.getPermsDoModulo('VENDAS').some(p =>
    p === 'COMPLETO' || p.includes('VISUALIZACAO')
  )
})

const carregarProdutos = async () => {
  if (!item.value.idContratoVenda) {
    produtosContrato.value = []
    return
  }
  isLoadingProdutos.value = true
  try {
    const res = await api.get(`/ContratoVendaProdutos/contrato/${item.value.idContratoVenda}`)
    produtosContrato.value = res.data
  } catch (e) {
    emit('show-notification', { message: 'Erro ao carregar produtos.', type: 'error' })
  } finally {
    isLoadingProdutos.value = false
  }
}

const carregarCondicoesPagamento = async () => {
  if (!item.value.idContratoVenda) {
    condicoesPagamentoContrato.value = []
    return
  }
  isLoadingCondicoes.value = true
  try {
    const res = await api.get(`/ContratoVendaCondicoesPg/contrato/${item.value.idContratoVenda}`)
    condicoesPagamentoContrato.value = res.data
  } catch (e) {
    emit('show-notification', { message: 'Erro ao carregar condições de pagamento.', type: 'error' })
  } finally {
    isLoadingCondicoes.value = false
  }
}

const carregarPedidosVenda = async () => {
  const idContrato = item.value.idContratoVenda
  if (!idContrato) return

  isLoadingPedidosVenda.value = true
  try {
    const res = await api.get('/PedidoVendas/por-contrato-venda/' + idContrato)
    pedidosVendaContrato.value = res.data
  } catch (e) {
    emit('show-notification', { message: 'Erro ao carregar pedidos de venda do contrato.', type: 'error' })
  } finally {
    isLoadingPedidosVenda.value = false
  }
}

const abrirPedidoVenda = (pedido = null, funcaoAdicional = null) => {
  const contextoPai = {
    idContratoVenda: item.value.idContratoVenda,
    idFilial: item.value.idFilial,
    idParceiroNegocioCliente: item.value.idParceiroNegocioCliente,
    lstMoeda: item.value.lstMoeda,
    atributosPrecificacaoJson: item.value.atributosPrecificacaoJson
  }

  // Adição vinda do contrato: abre em modo de espera (prePreencher = null)
  if (!pedido) {
    emit('drilldown-push', {
      type: 'pedido',
      id: null,
      props: {
        itemParaEditar: null,
        funcao: funcaoAdicional || 'adicao',
        prePreencher: null,
        contextoPai
      }
    })
    return
  }

  // Edição de um pedido de venda existente
  const propsBase = {
    itemParaEditar: pedido,
    contextoPai
  }
  if (funcaoAdicional) {
    propsBase.funcao = funcaoAdicional
  }
  emit('drilldown-push', {
    type: 'pedido',
    id: pedido?.idPedidoVenda,
    props: propsBase
  })
}

const handlePipelineClick = (etapa) => {
  if (etapa.key === 'pedido') {
    abrirPedidoVenda()
  } else {
    emit('pipeline-navegar', etapa.key)
  }
}

const configBuscaFilial = computed(() => ({
  ...filialSearch,
  titulo: 'Buscar Filial',
  filtrosFixos: {
    idEmpresa: authStore.empresaAtiva
  }
}))

const configBuscaCliente = computed(() => ({
  ...criarConfigBuscaCliente(authStore.organizacaoAtiva),
  titulo: 'Buscar Cliente'
}))

const configBuscaTemplate = computed(() => ({
  ...templatePrecificacaoSearch,
  filtrosFixos: {
    lstStatus: ST.Ativo
  }
}))

const configBuscaProduto = computed(() => ({
  ...criarConfigBuscaProduto(),
  filtrosFixos: {
    ...criarConfigBuscaProduto().filtrosFixos,
    idEmpresa: authStore.empresaAtiva
  }
}))

const configBuscaUnidadeMedida = computed(() => {
  if (!produtoEmEdicao.value?.idProduto) return null
  return criarConfigBuscaUnidadeMedidaProduto(produtoEmEdicao.value.idProduto)
})

// --- Lógica de Pricing (Atributos Dinâmicos) ---
const modalBuscaTemplate = ref(false)
const templateTemporario = ref(null)
const pricingFields = ref([])
const valoresPricing = reactive({})

const sincronizarJsonParaValores = () => {
  try {
    const data = item.value.atributosPrecificacaoJson ? JSON.parse(item.value.atributosPrecificacaoJson) : {}

    Object.keys(data).forEach(key => {
      if (key !== '_templateInfo') {
        valoresPricing[key] = data[key]
      }
    })

    if (data._templateInfo) {
      pricingFields.value = data._templateInfo.atributos || []
    }
  } catch (e) {
    console.error('Erro ao sincronizar JSON de pricing:', e)
  }
}

watch(valoresPricing, (novosValores) => {
  const payload = {
    ...novosValores,
    _templateInfo: {
      atributos: pricingFields.value
    }
  }
  item.value.atributosPrecificacaoJson = JSON.stringify(payload)
}, { deep: true })

const isContratoSaved = ref(false)

watch(() => item.value.idContratoVenda, async (newId, oldId) => {
  isContratoSaved.value = !!(newId && newId > 0)

  if (isContratoSaved.value) {
    const isInitialLoad = oldId === undefined

    if (isInitialLoad) {
      accordionState.dadosBasicos = true
      accordionState.pricing = false
      accordionState.produtos = false
      accordionState.condicoesPagamento = false
    } else {
      accordionState.pricing = true
      accordionState.produtos = true
      accordionState.condicoesPagamento = true
    }

    carregarProdutos()
    carregarCondicoesPagamento()
    sincronizarJsonParaValores()
    await carregarPedidosVenda()
  }
}, { immediate: true })

const abrirBuscaTemplate = () => {
  modalBuscaTemplate.value = true
}

const onTemplateSelecionado = (template) => {
  templateTemporario.value = template
  const temValores = Object.values(valoresPricing).some(v => v !== null && v !== '' && v !== 0)

  if (temValores) {
    if (modalConfirmacaoTrocaRef.value) {
      modalConfirmacaoTrocaRef.value.show()
    }
  } else {
    aplicarTemplate(template)
  }
}

const aplicarTemplate = (template) => {
  Object.keys(valoresPricing).forEach(key => delete valoresPricing[key])

  const atributos = template.estruturaJsonSchema ? JSON.parse(template.estruturaJsonSchema) : []
  pricingFields.value = atributos

  atributos.forEach(attr => {
    valoresPricing[attr.chave] = null
  })

  modalBuscaTemplate.value = false
}

const confirmarTrocaTemplate = (confirmed) => {
  if (confirmed && templateTemporario.value) {
    aplicarTemplate(templateTemporario.value)
  }
}

const carregarListas = async () => {
  try {
    const result = await metadataStore.carregarListasParaTabelas(['ContratoVenda', 'ContratoVendaProduto', 'ContratoVendaCondicaoPg', 'Produto'])
    Object.assign(listas, result)

    if (!isEditing.value && listas.LstStatusContratoVenda?.length > 0 && !item.value.lstStatusContratoVenda) {
      item.value.lstStatusContratoVenda = ''
    }
  } catch (e) {
    console.error('Erro ao carregar listas auxiliares via metadados:', e)
    emit('show-notification', { message: 'Erro ao carregar listas auxiliares via metadados.', type: 'error' })
  }
}

const toggleAccordion = (key) => {
  accordionState[key] = !accordionState[key]
}

const refCampoBuscaProduto = ref(null)

const iniciarNovoProduto = () => {
  if (!item.value.idParceiroNegocioCliente) {
    emit('show-notification', { message: 'Selecione um cliente antes de adicionar produtos.', type: 'warning' })
    return
  }

  console.log('[DEBUG TOLERANCIA] iniciarNovoProduto - item.value.percentualTolerancia:', item.value.percentualTolerancia, '| tipo:', typeof item.value.percentualTolerancia)
  console.log('[DEBUG TOLERANCIA] iniciarNovoProduto - item.value completo (trecho):', JSON.stringify({ percentualTolerancia: item.value.percentualTolerancia, idContratoVenda: item.value.idContratoVenda }))

  produtoEmEdicao.value = {
    idContratoVendaProduto: 0,
    idContratoVenda: item.value.idContratoVenda,
    idProduto: null,
    _originalIdProduto: null,
    nomeProduto: '',
    sku: '',
    lstNaturezaItem: '',
    idTipoProduto: null,
    nomeTipoProduto: '',
    quantidadeUnClienteNegociada: null,
    lstUnidadeMedidaNegociada: '',
    fatorConversao: 1,
    quantidadeUnBasicaNegociada: null,
    precoUnitarioAcordado: null,
    percentualTolerancia: item.value.percentualTolerancia
  }

  console.log('[DEBUG TOLERANCIA] produtoEmEdicao criado - percentualTolerancia:', produtoEmEdicao.value.percentualTolerancia)

  nextTick(() => {
    if (refCampoBuscaProduto.value) {
      refCampoBuscaProduto.value.abrirModalDeBusca()
    }
  })
}

const onProdutoSelecionado = async (p) => {
  if (!produtoEmEdicao.value) return

  console.log('[DEBUG TOLERANCIA] onProdutoSelecionado - ANTES de limpar campos. percentualTolerancia atual:', produtoEmEdicao.value.percentualTolerancia)

  produtoEmEdicao.value.nomeProduto = p.nome
  produtoEmEdicao.value.sku = p.sku
  produtoEmEdicao.value.lstNaturezaItem = p.lstNaturezaItem || ''
  produtoEmEdicao.value.idTipoProduto = p.idTipoProduto || null
  produtoEmEdicao.value.nomeTipoProduto = p.nomeTipoProduto || ''

  if (produtoEmEdicao.value._originalIdProduto === p.idProduto) {
    produtoEmEdicao.value._originalIdProduto = null
    return
  }

  produtoEmEdicao.value.quantidadeUnClienteNegociada = null
  produtoEmEdicao.value.quantidadeUnBasicaNegociada = null
  produtoEmEdicao.value.precoUnitarioAcordado = null
  // NÃO reseta percentualTolerancia - deve preservar o valor herdado do contrato
  console.log('[DEBUG TOLERANCIA] onProdutoSelecionado - DEPOIS. percentualTolerancia preservado:', produtoEmEdicao.value.percentualTolerancia)
}

const onCancelarBuscaProduto = () => {
  if (produtoEmEdicao.value && !produtoEmEdicao.value.idProduto && !produtoEmEdicao.value.idContratoVendaProduto) {
    produtoEmEdicao.value = null
  }
}

const onUnidadeMedidaSelecionado = (item) => {
  if (!produtoEmEdicao.value) return
  // item.lstUnidadeMedidaTexto contém o código da lista (modelValue)
  // item.lstUnidadeMedidaExibicao contém o texto para exibição
  // item.lstUnidadeMedidaDescricao contém a descrição para o label informativo
  produtoEmEdicao.value.lstUnidadeMedidaNegociada = item.lstUnidadeMedidaTexto
  produtoEmEdicao.value.fatorConversao = Number(item.fatorConversao) || 1
}

const parseNumber = (val) => Number(String(val).replace(',', '.')) || 0

const editarProduto = (prod) => {
  produtoEmEdicao.value = {
    ...prod,
    _originalIdProduto: prod.idProduto
  }
}

const salvarProduto = async () => {
  const p = { ...produtoEmEdicao.value }
  if (!item.value.idContratoVenda) {
    emit('show-notification', { message: 'Por favor, salve o contrato antes de adicionar produtos.', type: 'warning' })
    return
  }
  if (!p.idProduto) {
    emit('show-notification', { message: 'O produto é obrigatório.', type: 'warning' })
    return
  }

  const payload = {
    ...p,
    idContratoVenda: item.value.idContratoVenda,
    quantidadeUnClienteNegociada: Number(String(p.quantidadeUnClienteNegociada).replace(',', '.')) || 0,
    lstUnidadeMedidaNegociada: p.lstUnidadeMedidaNegociada || '',
    fatorConversao: Number(String(p.fatorConversao).replace(',', '.')) || 1,
    quantidadeUnBasicaNegociada: Number(String(p.quantidadeUnBasicaNegociada).replace(',', '.')) || 0,
    precoUnitarioAcordado: Number(String(p.precoUnitarioAcordado).replace(',', '.')) || 0,
    percentualTolerancia: Number(String(p.percentualTolerancia).replace(',', '.')) || 0
  }

  try {
    if (p.idContratoVendaProduto) {
      await api.put(`/ContratoVendaProdutos/${p.idContratoVendaProduto}`, payload)
    } else {
      await api.post('/ContratoVendaProdutos', payload)
    }
    emit('show-notification', { message: 'Produto salvo com sucesso.', type: 'success' })
    produtoEmEdicao.value = null
    await carregarProdutos()
  } catch (e) {
    emit('show-notification', { message: 'Erro ao salvar produto.', type: 'error' })
  }
}

const solicitarExclusaoProduto = (prod) => {
  modalExclusaoProduto.value.id = prod.idContratoVendaProduto
  if (modalExclusaoProdutoRef.value) {
    modalExclusaoProdutoRef.value.show()
  }
}

const confirmarExclusaoProduto = async (confirmed) => {
  if (!confirmed) return
  try {
    await api.delete(`/ContratoVendaProdutos/${modalExclusaoProduto.value.id}`)
    emit('show-notification', { message: 'Produto removido com sucesso.', type: 'success' })
    await carregarProdutos()
  } catch (e) {
    emit('show-notification', { message: 'Erro ao remover produto.', type: 'error' })
  }
}

const labelInfoPrazoDias = 'Informe um valor negativo para "Antes de"'

const iniciarNovaCondicao = () => {
  if (!item.value.idContratoVenda) {
    emit('show-notification', { message: 'Por favor, salve o contrato antes de adicionar condições de pagamento.', type: 'warning' })
    return
  }

  condicaoEmEdicao.value = {
    idContratoVendaCondicaoPg: 0,
    idContratoVenda: item.value.idContratoVenda,
    percentualDoTotal: null,
    descricao: '',
    lstReferenciaTemporal: '',
    lstCondPgMomento: 'COND_PG_MOMENTO1',
    lstCondPgUnidadeTempo: 'COND_PG_UNIDADE_TEMPO1',
    lstCondPgTipoCalculo: 'COND_PG_TIPO_CALCULO1',
    prazoDias: null
  }
}

const editarCondicao = (cond) => {
  condicaoEmEdicao.value = { ...cond }
}

const salvarCondicao = async () => {
  const c = { ...condicaoEmEdicao.value }

  if (!c.lstReferenciaTemporal || c.percentualDoTotal === null) {
    emit('show-notification', { message: 'Preencha todos os campos obrigatórios da condição de pagamento.', type: 'warning' })
    return
  }

  c.descricao = gerarDescricaoCondicao(c)

  const payload = {
    ...c,
    percentualDoTotal: Number(String(c.percentualDoTotal).replace(',', '.')) || 0,
    lstCondPgMomento: c.lstCondPgMomento || 'COND_PG_MOMENTO1',
    lstCondPgUnidadeTempo: c.lstCondPgUnidadeTempo || 'COND_PG_UNIDADE_TEMPO1',
    lstCondPgTipoCalculo: c.lstCondPgTipoCalculo || 'COND_PG_TIPO_CALCULO1',
    prazoDias: Math.abs(Number(c.prazoDias) || 0)
  }

  try {
    if (c.idContratoVendaCondicaoPg) {
      await api.put(`/ContratoVendaCondicoesPg/${c.idContratoVendaCondicaoPg}`, payload)
    } else {
      await api.post('/ContratoVendaCondicoesPg', payload)
    }
    emit('show-notification', { message: 'Condição de pagamento salva com sucesso.', type: 'success' })
    condicaoEmEdicao.value = null
    await carregarCondicoesPagamento()
  } catch (e) {
    emit('show-notification', { message: 'Erro ao salvar condição de pagamento.', type: 'error' })
  }
}

const solicitarExclusaoCondicao = (cond) => {
  modalExclusaoCondicao.value.id = cond.idContratoVendaCondicaoPg
  if (modalExclusaoCondicaoRef.value) {
    modalExclusaoCondicaoRef.value.show()
  }
}

const confirmarExclusaoCondicao = async (confirmed) => {
  if (!confirmed) return
  try {
    await api.delete(`/ContratoVendaCondicoesPg/${modalExclusaoCondicao.value.id}`)
    emit('show-notification', { message: 'Condição de pagamento removida com sucesso.', type: 'success' })
    await carregarCondicoesPagamento()
  } catch (e) {
    emit('show-notification', { message: 'Erro ao remover condição de pagamento.', type: 'error' })
  }
}

const obterCorStatus = (textoStatus) => {
  const s = String(textoStatus || '').toUpperCase()
  if (s.includes('CONCLUÍDO') || s.includes('FINALIZADO') || s.includes('FATURADO')) return 'badge-ghost-success'
  if (s.includes('ABERTO') || s.includes('ORÇAMENTO') || s.includes('DIGITAÇÃO')) return 'badge-ghost-warning'
  if (s.includes('CANCELADO')) return 'badge-ghost-danger'
  if (s.includes('CONFIRMADO')) return 'badge-ghost-info'
  return 'badge-ghost-secondary'
}

const getTextoLista = (lista, id) => {
  if (!lista) return id || '-'
  const found = lista.find(l => l.idListaClienteItem === id || l.idListaSistemaItem === id)
  return found ? found.textoLista : (id || '-')
}

const gerarDescricaoCondicao = (cond) => {
  if (!cond) return ''
  const prazo = Math.abs(Number(cond.prazoDias) || 0)
  const refTemporal = getTextoLista(listas.LstReferenciaTemporal, cond.lstReferenciaTemporal)
  const percentual = cond.percentualDoTotal || 0
  const momento = getTextoLista(listas.LstCondPgMomento, cond.lstCondPgMomento)
  const unidade = getTextoLista(listas.LstCondPgUnidadeTempo, cond.lstCondPgUnidadeTempo)
  const tipoCalc = getTextoLista(listas.LstCondPgTipoCalculo, cond.lstCondPgTipoCalculo)
  return `${percentual}${tipoCalc} ${prazo} ${unidade} ${momento} ${refTemporal}`
}

const formatarQtd = (valor) => {
  if (valor === null || valor === undefined || valor === '') return '-'
  const num = typeof valor === 'string' ? parseNumber(valor) : Number(valor)
  if (isNaN(num)) return valor
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4
  }).format(num)
}

const formatarMoeda = (valor) => {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor || 0)
}

const formatarDecimal = (valor) => {
  return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(valor || 0)
}

const getTextoUnidade = (id) => {
  const lista = listas.LstUnidadeMedida || []
  const un = lista.find(u => u.idListaClienteItem === id)
  return un ? un.textoLista : (id || '-')
}

const validarRegrasDeNegocio = async () => {
  const labelsObrigatorios = document.querySelectorAll('label.is-required')
  let hasBaseErrors = false

  for (const key in errors.value) {
    delete errors.value[key]
  }

  labelsObrigatorios.forEach(label => {
    if (label.offsetParent === null) return
    const inputId = label.getAttribute('for')
    if (!inputId) return
    const inputElement = document.getElementById(inputId)

    let isEmpty = false
    if (inputElement.tagName === 'SELECT') {
      isEmpty = !inputElement.value || inputElement.value === ''
    } else {
      isEmpty = !inputElement.value || String(inputElement.value).trim() === ''
    }

    if (isEmpty) {
      const nomeCampoLimpo = label.innerText.replace('*', '').trim()
      errors.value[inputId] = `O campo "${nomeCampoLimpo}" é de preenchimento obrigatório.`
      hasBaseErrors = true
    }
  })

  if (hasBaseErrors) {
    emit('show-notification', { message: 'Por favor, preencha todos os campos obrigatórios destacados em vermelho.', type: 'warning' })
    if (focarPrimeiroErro) await focarPrimeiroErro()
    return false
  }

  if (pricingFields.value.length === 0) {
    emit('show-notification', {
      message: 'A seleção de um template de precificação é obrigatória para salvar o contrato.',
      type: 'warning'
    })
    btnTemplate.value?.focus()
    return false
  }

  for (const campo of pricingFields.value) {
    const valor = valoresPricing[campo.chave]
    if (valor === null || valor === undefined || valor === '') {
      emit('show-notification', {
        message: `Preencha o campo "${campo.label || campo.chave}" do Pricing.`,
        type: 'warning'
      })
      return false
    }
  }

  if (!item.value.valorTotalContrato || Number(item.value.valorTotalContrato) <= 0) {
    emit('show-notification', {
      message: 'O Valor Total Efetivo (Final) é obrigatório e deve ser maior que zero.',
      type: 'warning'
    })
    return false
  }

  return true
}

const montarPayload = () => {
  const payload = {
    ...item.value,
    idFilial: item.value.idFilial ? Number(item.value.idFilial) : null,
    idParceiroNegocioCliente: item.value.idParceiroNegocioCliente ? Number(item.value.idParceiroNegocioCliente) : null,
    valorTotalContrato: Number(item.value.valorTotalContrato) || 0
  }

  delete payload.nomeCliente
  return payload
}

const salvarEContinuar = async () => {
  if (!temContratosVendaCompleto.value || isLoading.value) return

  const isValid = await validarRegrasDeNegocio()
  if (!isValid) return

  isLoading.value = true
  const payload = montarPayload()

  try {
    const res = item.value.idContratoVenda
      ? await api.put(`/ContratosVenda/${item.value.idContratoVenda}`, payload)
      : await api.post('/ContratosVenda', payload)

    item.value = res.data

    emit('show-notification', { message: 'Dados básicos salvos com sucesso! Adicione os produtos e condições.', type: 'success' })
  } catch (error) {
    console.error('Erro ao salvar e continuar:', error)
    let msg = 'Erro ao salvar o contrato.'
    if (error.response?.data?.errors) {
      msg = Object.values(error.response.data.errors).flat()[0] || msg
    } else if (error.response?.data?.message || error.response?.data?.title) {
      msg = error.response.data.message || error.response.data.title
    } else if (typeof error.response?.data === 'string') {
      msg = error.response.data
    }
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isLoading.value = false
  }
}

const salvar = async () => {
  if (!temContratosVendaCompleto.value || isLoading.value) return

  const isValid = await validarRegrasDeNegocio()
  if (!isValid) return

  const payload = montarPayload()

  await submitForm('ContratosVenda', 'idContratoVenda', payload)
}

const salvarAlteracoes = async () => {
  if (!temContratosVendaCompleto.value || isLoading.value) return

  const isValid = await validarRegrasDeNegocio()
  if (!isValid) return

  const payload = montarPayload()
  try {
    isLoading.value = true
    const res = item.value.idContratoVenda
      ? await api.put(`/ContratosVenda/${item.value.idContratoVenda}`, payload)
      : await api.post('/ContratosVenda', payload)

    if (res?.data) {
      initializeForm(res.data)
    }
    emit('show-notification', { message: 'Alterações salvas com sucesso!', type: 'success' })
  } catch (error) {
    let msg = 'Erro ao salvar alterações.'
    if (error.response?.data?.errors) {
      msg = Object.values(error.response.data.errors).flat()[0] || msg
    } else if (error.response?.data?.message || error.response?.data?.title) {
      msg = error.response.data.message || error.response.data.title
    } else if (typeof error.response?.data === 'string') {
      msg = error.response.data
    }
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isLoading.value = false
  }
}

const abrirAuditoria = () => {
  mostrarAuditoria.value = true
}

onMounted(async () => {
  await carregarListas()
})
</script>

<template>
  <div class="contrato-venda-form-wrapper" v-bind="$attrs">
    <SemAcessoModulo v-if="!temModuloVendas" titulo="Vendas" />
    <template v-if="temModuloVendas">
    <FormularioCadastro
      module-name="Contrato de Venda"
      :is-loading="isLoading"
      :is-editing="isEditing"
      :entity-info="entityInfoContrato"
      :titulo-registro="item.numeroContratoCliente"
      :ocultar-salvar="!isContratoSaved || !temContratosVendaCompleto"
      @submit="salvar"
      @submit-continuar="salvarAlteracoes"
      @cancelar="handleCancelDebug"
      @show-notification="(p) => emit('show-notification', p)"
    >
      <!-- PIPELINE DE STATUS (PROGRESSO) -->
      <ComexPipelineStatus
        v-if="drilldownMode"
        v-bind="pipelineProps"
        @navegar="handlePipelineClick"
      />

      <template #acoes-header v-if="isEditing">
        <button
          type="button"
          class="btn-icon-minimalista me-3"
          @click="abrirAuditoria"
          title="Ver Histórico de Alterações"
        >
          <i class="bi bi-clock-history"></i>
        </button>
      </template>

      <div v-if="!podeEditar" class="alert alert-warning border-0 rounded-3 mb-4 d-flex align-items-center">
        <i class="bi bi-lock-fill fs-4 me-3 text-warning"></i>
        <div><strong>Modo de Leitura:</strong> O seu perfil não tem permissões para editar este registo.</div>
      </div>

      <!-- FRAME 1: DADOS BÁSICOS -->
      <div class="card mb-3 border-success-subtle">
        <div class="card-header bg-success-subtle d-flex justify-content-between align-items-center cursor-pointer" @click="toggleAccordion('dadosBasicos')">
          <h5 class="mb-0 text-success-emphasis"><i class="bi bi-file-earmark-text me-2"></i>Dados Básicos</h5>
          <i class="bi" :class="accordionState.dadosBasicos ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
        </div>
        <div v-show="accordionState.dadosBasicos" class="card-body border-top border-success-subtle">
          <div class="row g-3">
            <div class="col-md-2">
              <CampoInput
                id="codigoContratoVenda"
                label="Código Contrato"
                v-model="item.codigoContratoVenda"
                disabled
                :descricao-informativa="!item.codigoContratoVenda ? 'Gerado automaticamente pelo sistema' : undefined"
              />
            </div>
            <div class="col-md-1">
              <CampoBusca
                id="idFilial"
                label="Filial"
                v-model="item.idFilial"
                :configuracao-busca="configBuscaFilial"
                required
                :disabled="!podeEditar"
              />
            </div>
            <div class="col-md-3">
              <CampoBusca
                id="idParceiroNegocioCliente"
                label="Cliente"
                v-model="item.idParceiroNegocioCliente"
                :configuracao-busca="configBuscaCliente"
                required
                :disabled="!podeEditar"
              />
            </div>
            <div class="col-md-2">
              <CampoInput
                id="numeroContratoCliente"
                label="Número Contrato Cliente"
                v-model="item.numeroContratoCliente"
                required
                :disabled="!podeEditar"
              />
            </div>
            <div class="col-md-1">
              <CampoInput
                id="dataContrato"
                label="Data do Contrato"
                v-model="item.dataContrato"
                type="date"
                required
                :disabled="!podeEditar"
              />
            </div>
            <div class="col-md-1">
              <CampoSelect
                id="lstMoeda"
                label="Moeda"
                v-model="item.lstMoeda"
                :options="listas.LstMoeda || []"
                option-label="textoLista"
                option-value="idListaClienteItem"
                required
                :disabled="!podeEditar"
              />
            </div>
            <div class="col-md-2">
              <CampoInput
                id="percentualTolerancia"
                label="Tolerância Quantitativa (%)"
                v-model="item.percentualTolerancia"
                type="decimal"
                :disabled="!podeEditar"
              />
            </div>
            <div class="col-md-2">
              <CampoSelect
                id="lstStatusContratoVenda"
                label="Status do Contrato"
                v-model="item.lstStatusContratoVenda"
                :options="listas.LstStatusContratoVenda || []"
                option-label="textoLista"
                option-value="idListaClienteItem"
                required
                :disabled="!podeEditar"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- FRAME 2: PRICING -->
      <div class="card mb-3 border-success-subtle">
        <div class="card-header bg-success-subtle d-flex justify-content-between align-items-center cursor-pointer" @click="toggleAccordion('pricing')">
          <h5 class="mb-0 text-success-emphasis fw-bold">
            <i class="bi bi-currency-dollar me-2"></i>Pricing
            <span v-if="item.valorTotalContrato" class="ms-3 fw-normal fs-6 badge bg-success bg-opacity-10 text-success-emphasis border border-success-subtle">
              Total: {{ formatarMoeda(item.valorTotalContrato) }}
            </span>
          </h5>
          <div class="d-flex align-items-center">
            <button
              v-if="temContratosVendaCompleto"
              ref="btnTemplate"
              type="button"
              class="btn btn-sm btn-success me-3"
              @click.stop="abrirBuscaTemplate"
            >
              <i class="bi" :class="pricingFields.length > 0 ? 'bi-arrow-repeat' : 'bi-plus-lg'"></i>
              {{ pricingFields.length > 0 ? 'Trocar Template' : 'Adicionar Template' }}
            </button>
            <i class="bi" :class="accordionState.pricing ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
          </div>
        </div>
        <div v-show="accordionState.pricing" class="card-body border-top border-success-subtle">
          <div v-if="pricingFields.length === 0" class="text-center py-4 text-muted">
            <i class="bi bi-info-circle me-2"></i>Selecione um template de precificação para habilitar os campos.
          </div>
          <div v-else class="row g-3">
            <div v-for="campo in pricingFields" :key="campo.chave" class="col-md-4">
              <CampoInput
                :id="campo.chave"
                :label="campo.label || ' '"
                v-model="valoresPricing[campo.chave]"
                required
                :disabled="!podeEditar"
              />
            </div>

            <div class="col-12"><hr class="my-2"></div>
            <div class="col-md-4 ms-auto">
              <CampoInput
                id="valorTotalContrato"
                label="Valor Total do Contrato"
                v-model="item.valorTotalContrato"
                type="moeda"
                required
                :disabled="!podeEditar"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- BOTÃO DE TRANSIÇÃO (SALVAR E CONTINUAR) -->
      <div class="mb-4 text-center" v-if="!isContratoSaved && temContratosVendaCompleto">
        <button
          type="button"
          @click.prevent="salvarEContinuar"
          class="btn btn-success px-4 py-2 fw-bold"
          :disabled="isLoading"
        >
          <span v-if="isLoading" class="spinner-border spinner-border-sm me-2"></span>
          <i v-if="!isLoading" class="bi bi-chevron-double-down me-1"></i>
          Salvar e Continuar
          <i v-if="!isLoading" class="bi bi-chevron-double-down ms-1"></i>
        </button>
        <div class="form-text mt-2">
          <i class="bi bi-info-circle me-1"></i> Salve os dados básicos para habilitar o cadastro de Produtos e Condições.
        </div>
      </div>

      <!-- FIELDSET CONDICIONAL PARA BLOQUEIO VISUAL ANTES DO PRIMEIRO SALVAMENTO -->
      <fieldset :disabled="!isContratoSaved" class="mb-3">
        <!-- FRAME 3: PRODUTOS DO CONTRATO -->
        <div class="card mb-3 border-success-subtle" :class="{'frame-bloqueado': !isContratoSaved}">
          <div class="card-header bg-success-subtle d-flex justify-content-between align-items-center" :class="{'cursor-pointer': isContratoSaved}" @click="isContratoSaved && toggleAccordion('produtos')">
            <h5 class="mb-0 text-success-emphasis fw-bold">
              <i class="bi bi-box-seam me-2"></i>Produtos do Contrato
              <span v-if="produtosContrato.length > 0" class="ms-3 fw-normal fs-6 badge bg-success bg-opacity-10 text-success-emphasis border border-success-subtle">
                {{ produtosContrato.length }} {{ produtosContrato.length === 1 ? 'produto cadastrado' : 'produtos cadastrados' }}
              </span>
            </h5>
            <div class="d-flex align-items-center">
              <button
                v-if="temContratosVendaCompleto"
                type="button"
                class="btn btn-sm btn-success me-3"
                @click.stop="iniciarNovoProduto"
              >
                <i class="bi bi-plus-lg"></i> Adicionar Produto
              </button>
              <i class="bi" :class="accordionState.produtos ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
            </div>
          </div>
          <div v-show="accordionState.produtos" class="card-body p-0 border-top border-success-subtle">
            <div v-if="isLoadingProdutos" class="text-center py-4">
              <div class="spinner-border spinner-border-sm text-success me-2"></div>
              Carregando produtos...
            </div>
            <div v-else class="admin-table-container border-0 shadow-none mb-0">
              <table class="table admin-table m-0">
                <thead>
                  <tr>
                    <th>SKU</th>
                    <th>Produto</th>
                    <th class="text-end">Quantidade</th>
                    <th>Unidade</th>
                    <th class="text-end">Preço Unit.</th>
                    <th class="text-end">Total</th>
                    <th class="text-end" style="width: 100px;">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="prod in produtosContrato" :key="prod.idContratoVendaProduto">
                    <td><code>{{ prod.sku }}</code></td>
                    <td>{{ prod.nomeProduto }}</td>
                    <td class="text-end fw-bold">{{ formatarQtd(prod.quantidadeUnClienteNegociada) }}</td>
                    <td>{{ getTextoUnidade(prod.lstUnidadeMedidaNegociada) }}</td>
                    <td class="text-end">{{ formatarDecimal(prod.precoUnitarioAcordado) }}</td>
                    <td class="text-end fw-bold">{{ formatarDecimal(prod.quantidadeUnClienteNegociada * prod.precoUnitarioAcordado) }}</td>
                    <td class="text-end pe-3">
                      <button v-if="temContratosVendaCompleto || temVisualizacao" type="button" class="btn-icon" @click="editarProduto(prod)" :title="temContratosVendaCompleto ? 'Editar' : 'Visualizar'">
                        <i :class="temContratosVendaCompleto ? 'bi bi-pencil-fill' : 'bi bi-eye-fill text-secondary'"></i>
                      </button>
                      <button v-if="temContratosVendaCompleto" type="button" class="btn-icon text-danger" @click="solicitarExclusaoProduto(prod)" title="Excluir">
                        <i class="bi bi-trash-fill"></i>
                      </button>
                    </td>
                  </tr>
                  <tr v-if="produtosContrato.length === 0">
                    <td colspan="7" class="text-center py-4 text-muted">
                      <i class="bi bi-info-circle me-2"></i> Nenhum produto cadastrado neste contrato.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- FRAME 4: CONDIÇÕES DE PAGAMENTO -->
        <div class="card mb-3 border-success-subtle" :class="{'frame-bloqueado': !isContratoSaved}">
          <div class="card-header bg-success-subtle d-flex justify-content-between align-items-center" :class="{'cursor-pointer': isContratoSaved}" @click="isContratoSaved && toggleAccordion('condicoesPagamento')">
            <h5 class="mb-0 text-success-emphasis fw-bold">
              <i class="bi bi-credit-card me-2"></i>Condições de Pagamento
              <span v-if="condicoesPagamentoContrato.length > 0" class="ms-3 fw-normal fs-6 badge bg-success bg-opacity-10 text-success-emphasis border border-success-subtle">
                {{ condicoesPagamentoContrato.length }} {{ condicoesPagamentoContrato.length === 1 ? 'condição cadastrada' : 'condições cadastradas' }}
              </span>
            </h5>
            <div class="d-flex align-items-center">
              <button
                v-if="temContratosVendaCompleto"
                type="button"
                class="btn btn-sm btn-success me-3"
                @click.stop="iniciarNovaCondicao"
              >
                <i class="bi bi-plus-lg"></i> Adicionar Condição
              </button>
              <i class="bi" :class="accordionState.condicoesPagamento ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
            </div>
          </div>
          <div v-show="accordionState.condicoesPagamento" class="card-body p-0 border-top border-success-subtle">
            <div v-if="isLoadingCondicoes" class="text-center py-4">
              <div class="spinner-border spinner-border-sm text-success me-2"></div>
              Carregando condições...
            </div>
            <div v-else class="admin-table-container border-0 shadow-none mb-0">
              <table class="table admin-table m-0">
                <thead>
                  <tr>
                    <th>Descrição explicativa</th>
                    <th class="text-end" style="width: 100px;">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="cond in condicoesPagamentoContrato" :key="cond.idContratoVendaCondicaoPg">
                    <td>{{ gerarDescricaoCondicao(cond) }}</td>
                    <td class="text-end pe-3">
                      <button v-if="temContratosVendaCompleto || temVisualizacao" type="button" class="btn-icon" @click="editarCondicao(cond)" :title="temContratosVendaCompleto ? 'Editar' : 'Visualizar'">
                        <i :class="temContratosVendaCompleto ? 'bi bi-pencil-fill' : 'bi bi-eye-fill text-secondary'"></i>
                      </button>
                      <button v-if="temContratosVendaCompleto" type="button" class="btn-icon text-danger" @click="solicitarExclusaoCondicao(cond)" title="Excluir">
                        <i class="bi bi-trash-fill"></i>
                      </button>
                    </td>
                  </tr>
                  <tr v-if="condicoesPagamentoContrato.length === 0">
                    <td colspan="2" class="text-center py-4 text-muted">
                      <i class="bi bi-info-circle me-2"></i> Nenhuma condição de pagamento cadastrada.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <!-- GRID DE PEDIDOS DE VENDA DO CONTRATO (colapsável) -->
        <div v-if="item.idContratoVenda" class="card mb-3 border-success-subtle" :class="{'frame-bloqueado': !isContratoSaved}">
          <div class="card-header bg-success-subtle d-flex justify-content-between align-items-center" :class="{'cursor-pointer': isContratoSaved}" @click="isContratoSaved && toggleAccordion('pedidosVenda')">
            <h6 class="mb-0 text-success-emphasis fw-bold d-flex align-items-center">
              <i class="bi bi-grid-3x3-gap me-2"></i>Pedidos de Venda do Contrato
              <span v-if="pedidosVendaContrato.length > 0" class="ms-3 fw-normal fs-6 badge bg-success bg-opacity-10 text-success-emphasis border border-success-subtle">
                {{ pedidosVendaContrato.length }} {{ pedidosVendaContrato.length === 1 ? 'pedido' : 'pedidos' }}
              </span>
            </h6>
            <div class="d-flex align-items-center gap-2">
              <button
                v-if="temContratosVendaCompleto"
                type="button"
                class="btn btn-sm btn-success text-white"
                @click.stop="abrirPedidoVenda"
              >
                <i class="bi bi-plus-lg"></i> Adicionar Pedido
              </button>
              <i class="bi fs-5" :class="accordionState.pedidosVenda ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
            </div>
          </div>
          <div v-show="accordionState.pedidosVenda" class="card-body p-0 border-top border-success-subtle">
            <div class="grid-scroll-container">
              <div v-if="isLoadingPedidosVenda" class="text-center py-4">
                <div class="spinner-border spinner-border-sm text-success me-2"></div>
                Carregando pedidos de venda...
              </div>
              <table v-else class="table table-hover admin-table mb-0">
                <thead>
                  <tr>
                    <th style="width: 80px;">ID</th>
                    <th>Nº Pedido</th>
                    <th>Emissão</th>
                    <th>Status</th>
                    <th class="text-end">Valor Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="pedido in pedidosVendaContratoOrdenados"
                    :key="pedido.idPedidoVenda"
                    class="cursor-pointer"
                    @click="temVisualizacao && abrirPedidoVenda(pedido)"
                  >
                    <td><code>#{{ pedido.idPedidoVenda }}</code></td>
                    <td><code>{{ pedido.numeroPedidoVenda || '---' }}</code></td>
                    <td>{{ formatarDataBR(pedido.dataEmissao) }}</td>
                    <td>
                      <span class="badge" :class="obterCorStatus(getTextoLista(listas.LstStatusPedido, pedido.lstStatusPedido))">
                        {{ getTextoLista(listas.LstStatusPedido, pedido.lstStatusPedido) }}
                      </span>
                    </td>
                    <td class="text-end fw-bold">{{ formatarDecimal(pedido.valorTotalNegociado) }}</td>
                  </tr>
                  <tr v-if="pedidosVendaContrato.length === 0">
                    <td colspan="5" class="text-center py-4 text-muted">Nenhum pedido de venda encontrado.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </fieldset>
    </FormularioCadastro>
    </template>

    <AuditLogViewer
      v-if="mostrarAuditoria"
      tabela="ContratoVenda"
      :chave-primaria="item.idContratoVenda"
      :titulo-registro="item.numeroContratoCliente"
      contexto="tenant"
      :tabelas-filhas="['ContratoVendaProduto', 'ContratoVendaCondicaoPg']"
      chave-estrangeira="IdContratoVenda"
      @fechar="mostrarAuditoria = false"
    />

    <!-- MODAIS DE PRICING -->
    <ModalDeBusca
      v-if="modalBuscaTemplate"
      :configuracao="configBuscaTemplate"
      @item-selecionado="onTemplateSelecionado"
      @fechar="modalBuscaTemplate = false"
    />

    <ConfirmationModal
      ref="modalConfirmacaoTrocaRef"
      title="Trocar Template"
      message="Ao trocar o template, os valores preenchidos no pricing atual serão perdidos. Deseja continuar?"
      @confirmed="confirmarTrocaTemplate"
    />

    <!-- MODAIS DE PRODUTO -->
    <ModalFormulario
      v-model="showModalProduto"
      :titulo="produtoEmEdicao?.idContratoVendaProduto ? 'Editar Produto do Contrato' : 'Adicionar Produto ao Contrato'"
      tamanho="lg"
      :ocultar-confirmar="!temContratosVendaCompleto"
      @confirmar="salvarProduto"
    >
      <div v-if="produtoEmEdicao" class="row g-3">
        <div class="col-md-12">
          <CampoBusca
            id="idProduto"
            label="Produto"
            v-model="produtoEmEdicao.idProduto"
            :configuracao-busca="configBuscaProduto"
            required
            ref="refCampoBuscaProduto"
            @selecionado="onProdutoSelecionado"
            @cancelar-busca="onCancelarBuscaProduto"
          />
        </div>

        <div v-if="configBuscaUnidadeMedida" class="col-md-4">
          <CampoBusca
            id="lstUnidadeMedidaNegociada"
            label="Unidade de Medida"
            v-model="produtoEmEdicao.lstUnidadeMedidaNegociada"
            :configuracao-busca="configBuscaUnidadeMedida"
            required
            @selecionado="onUnidadeMedidaSelecionado"
          />
        </div>

        <template v-if="produtoEmEdicao.idProduto">
          <div class="col-md-4">
            <CampoInput
              id="quantidadeUnClienteNegociada"
              label="Quantidade"
              v-model="produtoEmEdicao.quantidadeUnClienteNegociada"
              dynamic-decimals
              required
            />
          </div>
          <div class="col-md-4">
            <CampoInput
              id="percentualTolerancia"
              label="Tolerância Quantitativa (%)"
              v-model="produtoEmEdicao.percentualTolerancia"
              type="decimal"
            />
          </div>
        </template>
      </div>
    </ModalFormulario>

    <ConfirmationModal
      ref="modalExclusaoProdutoRef"
      title="Excluir Produto do Contrato"
      message="Deseja realmente remover este produto do contrato?"
      @confirmed="confirmarExclusaoProduto"
    />

    <!-- MODAIS DE CONDIÇÃO DE PAGAMENTO -->
    <ModalFormulario
      v-model="showModalCondicao"
      :titulo="condicaoEmEdicao?.idContratoVendaCondicaoPg ? 'Editar Condição de Pagamento' : 'Adicionar Condição de Pagamento'"
      tamanho="lg"
      :ocultar-confirmar="!temContratosVendaCompleto"
      @confirmar="salvarCondicao"
    >
      <div v-if="condicaoEmEdicao" class="row g-3">
        <div class="col-md-3">
          <CampoInput
            id="prazoDias"
            label="Prazo em Dias"
            v-model="condicaoEmEdicao.prazoDias"
            type="number"
            step="1"
            min="0"
            required
            :label-info="labelInfoPrazoDias"
          />
        </div>
        <div class="col-md-3">
          <CampoSelect
            id="lstCondPgMomento"
            label="Momento"
            v-model="condicaoEmEdicao.lstCondPgMomento"
            :options="listas.LstCondPgMomento || []"
            option-value="idListaSistemaItem"
            option-label="textoLista"
            required
          />
        </div>
        <div class="col-md-6">
          <CampoBuscaLista
            id="lstReferenciaTemporal"
            label="Referência Temporal"
            v-model="condicaoEmEdicao.lstReferenciaTemporal"
            tabela="ContratoVendaCondicaoPg"
            required
          />
        </div>
        <div class="col-md-3">
          <CampoInput
            id="percentualDoTotal"
            label="% do Total"
            v-model="condicaoEmEdicao.percentualDoTotal"
            type="moeda"
            required
          />
        </div>
        <div class="col-12 mt-3">
          <div class="alert alert-info py-2 mb-0">
            <i class="bi bi-info-circle me-2"></i>
            <strong>Descrição:</strong> {{ gerarDescricaoCondicao(condicaoEmEdicao) }}
          </div>
        </div>
      </div>
    </ModalFormulario>

    <ConfirmationModal
      ref="modalExclusaoCondicaoRef"
      title="Excluir Condição de Pagamento"
      message="Deseja realmente remover esta condição de pagamento?"
      @confirmed="confirmarExclusaoCondicao"
    />
  </div>
</template>

<style scoped>
/* === Tema Verde Escuro ContratoVenda === */
.contrato-venda-form-wrapper {
  --ct-green: #0f5c3a;
  --ct-green-bg: #0b4a2e;
  --ct-green-border: #1a7a4a;
  --ct-green-text: #ffffff;
}

.contrato-venda-form-wrapper .card-header.bg-success-subtle {
  background-color: var(--ct-green-bg) !important;
  color: var(--ct-green-text) !important;
}

.contrato-venda-form-wrapper .card-header.bg-success-subtle h5,
.contrato-venda-form-wrapper .card-header.bg-success-subtle .bi,
.contrato-venda-form-wrapper .card-header.bg-success-subtle span {
  color: var(--ct-green-text) !important;
}

.contrato-venda-form-wrapper .border-success-subtle {
  border-color: var(--ct-green-border) !important;
}

.contrato-venda-form-wrapper .btn-success {
  background-color: var(--ct-green) !important;
  border-color: var(--ct-green-border) !important;
  color: #ffffff !important;
}

.contrato-venda-form-wrapper .btn-success:hover {
  background-color: var(--ct-green-bg) !important;
  border-color: var(--ct-green) !important;
}

.contrato-venda-form-wrapper .btn-success .bi,
.contrato-venda-form-wrapper .btn-success i {
  color: #ffffff !important;
}

.contrato-venda-form-wrapper .text-success-emphasis {
  color: var(--ct-green) !important;
}

.contrato-venda-form-wrapper .badge.bg-success {
  background-color: var(--ct-green) !important;
}

.contrato-venda-form-wrapper .badge.bg-success.bg-opacity-10 {
  background-color: color-mix(in srgb, var(--ct-green) 10%, transparent) !important;
}

.contrato-venda-form-wrapper .badge.text-success-emphasis {
  color: var(--ct-green) !important;
}

.contrato-venda-form-wrapper .badge.border-success-subtle {
  border-color: var(--ct-green-border) !important;
}

/* Dark mode */
[data-bs-theme="dark"] .contrato-venda-form-wrapper {
  --ct-green: #1a8a4e;
  --ct-green-bg: #0d3d25;
  --ct-green-border: #2a9a5e;
  --ct-green-text: #a3cfb4;
}

[data-bs-theme="dark"] .contrato-venda-form-wrapper .card-header.bg-success-subtle {
  background-color: var(--ct-green-bg) !important;
  color: var(--ct-green-text) !important;
}

[data-bs-theme="dark"] .contrato-venda-form-wrapper .card-header.bg-success-subtle h5,
[data-bs-theme="dark"] .contrato-venda-form-wrapper .card-header.bg-success-subtle .bi,
[data-bs-theme="dark"] .contrato-venda-form-wrapper .card-header.bg-success-subtle span {
  color: var(--ct-green-text) !important;
}

.frame-bloqueado {
  opacity: 0.6;
  filter: grayscale(0.5);
  transition: all 0.3s ease;
}

.cursor-pointer {
  cursor: pointer;
}

.admin-table-container {
  background-color: transparent;
  border: 1px solid var(--bs-border-color);
  border-radius: 0.25rem;
  overflow-x: auto;
}
.admin-table thead th {
  background-color: var(--bs-tertiary-bg);
  color: var(--bs-heading-color);
  font-weight: 600;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid var(--bs-border-color);
}
.admin-table td {
  border-bottom: 1px solid var(--bs-border-color);
  color: var(--bs-body-color);
  padding: 0.5rem 0.75rem;
}
.btn-icon { background: none; border: none; color: #6c7d8e; padding: 0.4rem; transition: 0.2s; }
.btn-icon:hover { color: #212934; }
[data-bs-theme="dark"] .btn-icon:hover { color: #fff; }

.grid-scroll-container {
  max-height: 200px;
  overflow-y: auto;
  overflow-x: auto;
}

@media (max-width: 991.98px) {
  .grid-scroll-container {
    max-height: 150px;
    overflow-x: auto;
  }
}
</style>
